import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useToast } from "../../../context/ToastContext";
import { ProgressSpinner } from "primereact/progressspinner";
import { useTranslation } from "react-i18next";
import Paginator from "../../../components/Paginator/Paginator";
import FailureApi from "../../../services/FailureApi";
import ListCard from "../../../components/ListCard/ListCard";
import { Tag } from "../../../components/Tag/Tag";
import { formatDate } from "../../../utilities/date";

interface Version {
  id: string;
  failureId: string;
  failureVersion: number;
  dateVersioned: string;
  versionResponsibleId: string;
  versionResponsibleName?: string;
  actionPlanId: string;
  actionPlanName: string;
  severity: number;
  occurrence: number;
  detectability: number;
  createdAt: string;
  createdBy: string | null;
}

const FailureVersionHistoryTab: React.FC = () => {
  const { failureId } = useParams<{
    failureId: string;
  }>();
  const { displayToast } = useToast();
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(9);
  const [totalRecords, setTotalRecords] = useState(0);
  const { t } = useTranslation("form");

  const fetchVersions: any = async () => {
    if (!failureId) return;
    try {
      setLoading(true);
      const response = await FailureApi.GetVersionHistory(
        failureId,
        currentPage,
        pageSize,
      );
      setVersions(response.data.failureVersions);
      setTotalRecords(response.data.total);
    } catch (err) {
      displayToast("error", t("Error"), "", false, 3000);
      setError(t("errorloadMessage"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (failureId) {
      fetchVersions();
    }
  }, [failureId, currentPage]);

  const sortedVersions = [...versions].sort(
    (a, b) => b.failureVersion - a.failureVersion,
  );

  // formatDate local removed, using imported formatDate
  const calculateRPN: any = (s: number, o: number, d: number) => s * o * d;
  const activeVersion = Math.max(...versions.map((ver) => ver.failureVersion));

  return (
    <>
      {loading ? (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : sortedVersions.length === 0 ? (
        <div>
          <p>{t("noPreviousVersions")}</p>
        </div>
      ) : (
        <div className="grid">
          {sortedVersions.map((v) => {
            const isActive = v.failureVersion === activeVersion;

            return (
              <div key={v.id} className={`col-12 md:col-4`}>
                <ListCard inactive={!isActive} style={isActive ? { borderColor: 'var(--primary-color)' } : undefined}>
                  <ListCard.Header
                    title={`${t("version")} ${v.failureVersion}`}
                    subtitle={!v.actionPlanName ? t("newFailure") : v.actionPlanName}
                    identifier={formatDate(v.dateVersioned)}
                  >
                    {isActive && (
                      <Tag label={t("active") as string} severity="successInverse" />
                    )}
                  </ListCard.Header>

                  <ListCard.Content>
                    {v.versionResponsibleName && (
                      <ListCard.Metadata layout="stacked">
                        <ListCard.Metadata.Item icon="person" label={t("responsible") as string} value={v.versionResponsibleName} iconColor="primary" />
                        <ListCard.Metadata.Item icon="chat" label={t("reason") as string} value={!v.actionPlanName ? t("failureCreation") : t("actionPlanCompleted")} iconColor="primary" />
                      </ListCard.Metadata>
                    )}

                    <ListCard.Metadata layout="inline">
                      <ListCard.Metadata.Item icon="warning" label={t("severity") as string} value={v.severity} iconColor="warning" />
                      <ListCard.Metadata.Item icon="sync" label={t("occurrence") as string} value={v.occurrence} iconColor="info" />
                      <ListCard.Metadata.Item icon="visibility" label={t("detectability") as string} value={v.detectability} iconColor="success" />
                    </ListCard.Metadata>
                  </ListCard.Content>

                  <ListCard.Footer alignment="between" divider>
                    <ListCard.Metadata layout="inline">
                      <ListCard.Metadata.Item icon="calculate" label="RPN" value={calculateRPN(v.severity, v.occurrence, v.detectability)} iconColor="info" />
                    </ListCard.Metadata>
                  </ListCard.Footer>
                </ListCard>
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-4">
        <Paginator
          first={(currentPage - 1) * pageSize}
          rows={pageSize}
          totalRecords={totalRecords}
          onPageChange={(e) => setCurrentPage(Math.floor(e.first / e.rows) + 1)}
        />
      </div>
    </>
  );
};

export default FailureVersionHistoryTab;
